import { IS_LOCAL, browserInfo } from '../environment';
import { REPORT_TYPE, buildSSOPackage, getLoggerUrl, isUndefined } from '../utils';
import ErrorCode from '../../common/error-code';
import { getTimestamp } from '../time';
import RtcError from '../../common/rtc-error';
import { ENV_NAME, KIBANA_EVENT, LOGGER_CMD_TYPE, version } from '../../common/constants';
import loggerManager from './logger-manager';
import { http } from '../http';
import innerEmitter from '../../inner-emitter/inner-emitter';
import { INNER_EVENT } from '../../inner-emitter/inner-type';
import ssoManager from '../sso/sso-manager';

interface LogConfig {
  sdkAppId: string;
  userId: string;
  version?: string;
  env: string;
  browserVersion?: string;
  ua?: string;
}
interface LogEvent {
  delta?: number;
  eventType: typeof KIBANA_EVENT[keyof typeof KIBANA_EVENT] | string;
  code?: number;
  error?: any;
  result?: string;
  userId?: string;
  roomId?: string;
}
class EventLogger {
  _roomIdMap = new Map<string, string>(); // Map<userId, roomId>
  _configs: LogConfig;
  constructor() {
    // 用于上报 roomId
    if (typeof registerProcessor === 'undefined') this._configs = {
      sdkAppId: '',
      userId: '',
      version,
      env: ENV_NAME.QCLOUD,
      browserVersion: browserInfo.name + browserInfo.version,
      ua: navigator.userAgent
    };
  }

  setConfig({
    sdkAppId,
    env,
    userId,
    roomId
  }: LogConfig & { roomId?: string; }) {
    if (sdkAppId !== this._configs.sdkAppId) {
      // sdkappid 需要 string 类型，否则上报的 sdkappid 会被 kibana 忽略
      this._configs.sdkAppId = String(sdkAppId);
    }
    this._configs.env = env;
    this._configs.userId = userId;
    this._roomIdMap.set(userId, String(roomId));
  }

  logSuccessEvent(event: LogEvent) {
    if (IS_LOCAL || !loggerManager.isAbleToUpload) {
      return;
    }
    // 上报到 kibana，在 kibana 统计成功率，以及失败的错误信息
    if (this._configs.env === ENV_NAME.QCLOUD) {
      // collect cloud env's stats only
      this.uploadEventToKibana({ ...event, result: 'success' });
    }
  }

  logFailedEvent(event: LogEvent) {
    if (IS_LOCAL || !loggerManager.isAbleToUpload) {
      return;
    }
    const { eventType, code, error, userId } = event;
    const data = {
      roomId: this._roomIdMap.get(userId || this._configs.userId),
      userId,
      eventType,
      result: 'failed',
      code:
        code || error?.extraCode || error?.code || ErrorCode.UNKNOWN
    };
    // 上报到 kibana，在 kibana 统计成功率，以及失败的错误信息
    if (this._configs.env === ENV_NAME.QCLOUD) {
      // collect cloud env's stats only
      this.uploadEventToKibana({ ...data, error });
    }
  }

  uploadEventToKibana(event: LogEvent) {
    let msg = `stat-${event.eventType}-${event.result}`;
    if (
      event.eventType === 'delta-join' ||
      event.eventType === 'delta-leave' ||
      event.eventType === 'delta-publish'
    ) {
      msg = `${event.eventType}:${event.delta}`;
    }
    this.uploadEvent({ log: msg, userId: event.userId });

    if (event.result === 'failed') {
      msg = `stat-${event.eventType}-${event.result}-${event.code}`;
      this.uploadEvent({ log: msg, userId: event.userId, error: event.error });
    }
  }

  uploadEvent({
    log,
    userId,
    error
  }: {
    log: string;
    userId?: string;
    error?: RtcError | Error | MediaError;
  }) {
    const data : {
      timestamp: string,
      sdkAppId: string,
      userId: string,
      log: string,
      version?: string,
      errorInfo?: string
    } = {
      timestamp: getTimestamp(),
      sdkAppId: this._configs.sdkAppId,
      userId: userId || this._configs.userId,
      version,
      log
    };
    if (error) {
      data.errorInfo = error.message;
      // @ts-ignore
      if (error.stack) {
      // @ts-ignore
        data.errorInfo += `\n${error.stack}`;
      }
    }
    const toUpload = ssoManager.enable ? buildSSOPackage(data, REPORT_TYPE.LOG, Number(this._configs.sdkAppId)) : JSON.stringify(data);
    this.sendRequest(
      getLoggerUrl(this._configs.sdkAppId, LOGGER_CMD_TYPE.LOG),
      toUpload
    );
  }

  sendRequest(url: string, data: any) {
    setTimeout(() => http({ url, body: data, priority: 'low' }).catch(() => {
      // ignore
    }), 2000);
  }
}

export default new EventLogger();
